// Modernist — restrained editorial wedding paper-invitation catalog card.
//
// Aesop / Acne / Papier restraint applied to a centred card. The outer frame
// is a single tinted accent ring with a quiet inset hairline; inside, an
// editorial paper card with a small geometric medallion (intersecting
// rectangles — a deliberate, architectural mark), the TO'Y MAROSIMI
// eyebrow, names typeset in the template's display font, and a small-caps
// date + venue caption at the foot. All colors come from LayoutCtx.
import { Photo, type LayoutCtx } from "./_shared";

export function ModernistLayout({ names, date, venue, accent, ink, bg, font, photo, photos }: LayoutCtx) {
  const heroPhoto = photos[0] || photo;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `linear-gradient(180deg, ${accent}1a 0%, ${bg} 18%, ${bg} 82%, ${accent}1a 100%)`,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Top corner index — quiet meta */}
        <div
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 700,
            letterSpacing: "0.36em",
            color: `${ink}88`,
          }}
        >
          NO. 05
        </div>
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 700,
            letterSpacing: "0.36em",
            color: accent,
          }}
        >
          MMXXVI
        </div>

        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
            marginTop: "3%",
          }}
        >
          TO&apos;Y MAROSIMI
        </div>

        {/* Architectural medallion — photo plate or intersecting rectangles */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              overflow: "hidden",
              border: `1px solid ${accent}88`,
              background: `linear-gradient(160deg, ${accent}14, ${bg})`,
            }}
          >
            <Photo
              src={heroPhoto}
              sepia
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
              fallback={
                <svg viewBox="0 0 100 100" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} aria-hidden>
                  <rect x="20" y="20" width="60" height="60" fill="none" stroke={accent} strokeWidth="0.7" opacity={0.7} />
                  <rect x="32" y="32" width="36" height="36" fill="none" stroke={accent} strokeWidth="0.5" opacity={0.55} />
                  <line x1="20" y1="50" x2="80" y2="50" stroke={accent} strokeWidth="0.4" opacity={0.4} />
                  <line x1="50" y1="20" x2="50" y2="80" stroke={accent} strokeWidth="0.4" opacity={0.4} />
                  <circle cx="50" cy="50" r="2" fill={accent} />
                </svg>
              }
            />
          </div>
          {/* Outer accent corner brackets */}
          {[
            { top: -6, left: -6, b: "right" },
            { top: -6, right: -6, b: "left" },
            { bottom: -6, left: -6, b: "right" },
            { bottom: -6, right: -6, b: "left" },
          ].map((c, i) => (
            <span
              key={i}
              style={{
                position: "absolute",
                top: c.top,
                left: c.left,
                right: c.right,
                bottom: c.bottom,
                width: 12,
                height: 12,
                borderTop: c.top !== undefined ? `1px solid ${accent}` : undefined,
                borderBottom: c.bottom !== undefined ? `1px solid ${accent}` : undefined,
                borderLeft: c.left !== undefined ? `1px solid ${accent}` : undefined,
                borderRight: c.right !== undefined ? `1px solid ${accent}` : undefined,
              }}
            />
          ))}
        </div>

        {/* Names — minimal, restrained */}
        <div
          style={{
            fontFamily: font,
            fontSize: 30,
            lineHeight: 1.0,
            fontWeight: 400,
            letterSpacing: "-0.02em",
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Rule */}
        <div
          aria-hidden
          style={{ width: "30%", height: 1, background: `${ink}33`, marginTop: "4%" }}
        />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: 8,
          }}
        >
          TO&apos;Y MAROSIMI · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
